import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import GroupsAutocomplete from '../../../../shared/groups-autocomplete';
interface AddToGroupActionProps {
  description?: string;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  control: Control<RulesEngineFormValues>;
  watch: UseFormWatch<RulesEngineFormValues>;
  errors: FieldErrors<RulesEngineFormValues>;
}

import { RulesEngineFormValues } from '../rules-engine-form-page';
import ActionDescription from './action-description';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';

export default function AddToGroupAction({
  setValue,
  watch,
  errors,
  control,
  description,
}: AddToGroupActionProps) {
  const groupInfo = watch('groupInfo');
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const autocompleteKey = useRef(new Date().getTime().toString());
  const [alreadySelectedError, setAlreadySelectedError] = useState(false);

  function removeSelection(email: string, index: number) {
    const newList = groupInfo.groupEmail!.filter(
      (groupEmail) => groupEmail !== email
    );
    const newRoles = [...groupInfo.groupRole];
    newRoles.splice(index, 1);
    setValue(`groupInfo.groupEmail`, newList);
    setValue(`groupInfo.groupRole`, newRoles);
  }

  const memberRoleOptions = [
    { displayValue: 'Member', value: 'MEMBER' },
    { displayValue: 'Manager', value: 'MANAGER' },
    { displayValue: 'Owner', value: 'OWNER' },
  ];

  return (
    <Stack sx={{ p: 1 }}>
      <ActionDescription description={description} />
      <Stack gap={1}>
        <Stack flexDirection={'row'} gap={2}>
          <Box sx={{ width: 400 }}>
            <Controller
              rules={{
                required: 'Group is required.',
              }}
              name="groupInfo.groupEmail"
              control={control}
              render={() => (
                <GroupsAutocomplete
                  newKey={`${autocompleteKey.current}`}
                  autocompleteValue={autoCompleteResponse}
                  setAutoCompleteValue={setAutoCompleteResponse}
                  errors={
                    !!errors.groupInfo?.groupEmail || alreadySelectedError
                  }
                  helperText={
                    alreadySelectedError
                      ? 'Group is already selected.'
                      : errors.groupInfo?.groupEmail
                      ? errors?.groupInfo?.groupEmail?.message
                      : ' '
                  }
                  onSelection={(value) => {
                    if (!value) return;
                    if (groupInfo.groupEmail?.includes(value)) {
                      setAlreadySelectedError(true);
                    } else {
                      setAlreadySelectedError(false);
                      setValue(
                        `groupInfo.groupEmail`,
                        groupInfo.groupEmail
                          ? [...groupInfo.groupEmail, value]
                          : [value]
                      );
                      setValue(
                        `groupInfo.groupRole`,
                        groupInfo.groupRole
                          ? [...groupInfo.groupRole, 'MEMBER']
                          : ['MEMBER']
                      );
                      autocompleteKey.current = new Date().getTime().toString();
                      setAutoCompleteResponse('');
                    }
                  }}
                />
              )}
            />
          </Box>
        </Stack>
      </Stack>
      <Box sx={{ height: '100%' }}>
        <Paper sx={{ minWidth: 470, overflow: 'hidden', maxHeight: 190 }}>
          <TableContainer sx={{ minWidth: 470, maxHeight: 190 }}>
            <Table size={'small'} stickyHeader aria-label="searched-groups">
              <TableHead>
                <TableRow>
                  <TableCell>Selected Groups</TableCell>
                  <TableCell>Group Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupInfo.groupEmail
                  ? groupInfo.groupEmail.map((group, index) => (
                      <TableRow key={group}>
                        <TableCell>
                          <Stack
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography variant="body2">{group}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <TextField
                              inputProps={{
                                id: 'memberRole',
                              }}
                              InputLabelProps={{
                                htmlFor: 'memberRole',
                              }}
                              label="Member Role"
                              value={groupInfo.groupRole![index]}
                              onChange={(event) => {
                                const newRoles = [...groupInfo.groupRole];
                                newRoles[index] = event.target.value;
                                setValue(`groupInfo.groupRole`, newRoles);
                              }}
                              autoComplete="off"
                              select
                              sx={{ width: 115 }}
                            >
                              {memberRoleOptions.map((option) => (
                                <MenuItem
                                  value={option.value}
                                  key={option.displayValue}
                                >
                                  {option.displayValue}
                                </MenuItem>
                              ))}
                            </TextField>
                            <Tooltip arrow title="Delete">
                              <span>
                                <IconButton
                                  onClick={() => {
                                    removeSelection(
                                      groupInfo.groupEmail![index],
                                      index
                                    );
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Stack>
  );
}
